/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNull from 'lodash.isnull';
import {
  errorAddOptions,
  errorEditOptions,
  errorOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {toggleUserPrompt} from '@ion/applications/src/store/actions/user-prompt';
import {resolveDeviceFile} from '@ion/applications/src/utils/helpers';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  createDeviceFile,
  deleteDeviceFile,
  getFile,
  loadDeviceFiles,
  updateDeviceFile
} from '@ion/core/src/services/Connect';
import {
  AuditChange,
  DeviceFile,
  DeviceFileFilters,
  Entity
} from '@ion/core/src/types/entities';
import {LoadDeviceFilesParams} from '@ion/core/src/types/services';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {PageInitializeAction} from '@/types/redux';
import {SearchFields} from '../../routes/provisioning/devicefiles/useModularSearch';

// name
export const NAME                                   = 'devicefiles';

// initialise the grid
export const INITIALIZE_DEVICE_FILES                = 'INITIALIZE_DEVICE_FILES';
export const SET_GRID_COLUMNS                       = 'SET_DEVICE_FILES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                    = 'UPDATE_DEVICE_FILES_GRID_COLUMNS';

// refresh grid ...
export const DEVICE_FILES_GRID_REFRESHED            = 'DEVICE_FILES_GRID_REFRESHED';

export const AUTO_JOBS_GRID_REFRESHED               = 'AUTO_JOBS_GRID_REFRESHED';

export const SET_VIEW_SUB_GROUPS                    = 'SET_DEVICE_FILES_VIEW_SUB_GROUPS';

export const SET_VIEW_ONLY_THIS_ACCOUNT             = 'SET_DEVICE_FILES_VIEW_ONLY_THIS_ACCOUNT';

export const SET_GRID_PAGE_SIZE                     = 'SET_DEVICE_FILES_GRID_PAGE_SIZE';

// retrieve Device Files
export const GET_DEVICE_FILES_REQUEST               = 'GET_DEVICE_FILES_REQUEST';
export const GET_DEVICE_FILES_RESPONSE              = 'GET_DEVICE_FILES_RESPONSE';
export const GET_DEVICE_FILES_ERROR                 = 'GET_DEVICE_FILES_ERROR';

// Add device file
export const ADD_DEVICE_FILE                        = 'ADD_DEVICE_FILE';
export const ADD_DEVICE_FILE_REQUEST                = 'ADD_DEVICE_FILE_REQUEST';
export const ADD_DEVICE_FILE_RESPONSE               = 'ADD_DEVICE_FILE_RESPONSE';
export const ADD_DEVICE_FILE_ERROR                  = 'ADD_DEVICE_FILE_ERROR';

// View device file
export const VIEW_DEVICE_FILE                       = 'VIEW_DEVICE_FILE';
export const VIEW_DEVICE_FILE_REQUEST               = 'VIEW_DEVICE_FILE_REQUEST';
export const VIEW_DEVICE_FILE_RESPONSE              = 'VIEW_DEVICE_FILE_RESPONSE';
export const VIEW_DEVICE_FILE_ERROR                 = 'VIEW_DEVICE_FILE_ERROR';

// Edit device file
export const EDIT_DEVICE_FILE                       = 'EDIT_DEVICE_FILE';
export const EDIT_DEVICE_FILE_REQUEST               = 'EDIT_DEVICE_FILE_REQUEST';
export const EDIT_DEVICE_FILE_RESPONSE              = 'EDIT_DEVICE_FILE_RESPONSE';
export const EDIT_DEVICE_FILE_ERROR                 = 'EDIT_DEVICE_FILE_ERROR';

// Audit device file
export const AUDIT_DEVICE_FILE                      = 'AUDIT_DEVICE_FILE';
export const AUDIT_DEVICE_FILE_SHOW_CHANGES_DIALOG  = 'AUDIT_DEVICE_FILE_SHOW_CHANGES_DIALOG';

// View device files
export const VIEW_DEVICE_FILES                      = 'VIEW_DEVICE_FILES';

// search queries
export const UPDATE_DEVICE_FILE_SEARCH_QUERIES      = 'UPDATE_DEVICE_FILE_SEARCH_QUERIES';
export const RESET_DEVICE_FILE_SEARCH_QUERIES       = 'RESET_DEVICE_FILE_SEARCH_QUERIES';

export const DOWNLOAD_FILE                          = 'DOWNLOAD_FILE';
export const DOWNLOAD_RELEASE_NOTE                  = 'DOWNLOAD_RELEASE_NOTE';

export const DELETE_DEVICE_FILE                     = 'DELETE_DEVICE_FILE';
export const DELETE_DEVICE_FILE_REQUEST             = 'DELETE_DEVICE_FILE_REQUEST';
export const DELETE_DEVICE_FILE_RESPONSE            = 'DELETE_DEVICE_FILE_RESPONSE';
export const DELETE_DEVICE_FILE_ERROR               = 'DELETE_DEVICE_FILE_ERROR';

export const RESET_DEVICE_FILE_STATE                = 'RESET_DEVICE_FILE_STATE';

/**
 * @deviceFilesGridRefreshed
 */
export const deviceFilesGridRefreshed = (): PageInitializeAction => {
  return {type: DEVICE_FILES_GRID_REFRESHED};
};

/**
 * @autoJobsGridRefreshed
 */
export const autoJobsGridRefreshed = (): PageInitializeAction => {
  return {type: AUTO_JOBS_GRID_REFRESHED};
};

/**
 * @initialize
 */
export const initializeDeviceFile = (): PageInitializeAction => {
  return {type: INITIALIZE_DEVICE_FILES};
};

// Updates the modular search queries for Device File
export const updateDeviceFileSearchQueries = (searchParams: DeviceFileFilters) => ({
  type : UPDATE_DEVICE_FILE_SEARCH_QUERIES, searchParams
});

// Resets the Device File filters state
export const resetDeviceFileSearchQueries = () => ({
  type : RESET_DEVICE_FILE_SEARCH_QUERIES
});

// Resets any specified Device File redux state
export const resetDeviceFileState = () => ({
  type : RESET_DEVICE_FILE_STATE
});

/**
 * @addDeviceFile
 *
 * Invoked when the Add Device file editor is to be displayed or removed.
 */
export const addDeviceFile = (show = true) => ({
  type : ADD_DEVICE_FILE, show
});

/**
 * Invoked when the View Device file editor is to be displayed or removed.
 */
export const viewDeviceFile = (deviceFile: DeviceFile, show = true) => ({
  type : VIEW_DEVICE_FILE, deviceFile, show
});

/**
 * @editDeviceFile
 *
 * Invoked when the Edit Device file editor is to be displayed or removed.
 */
export const editDeviceFile = (deviceFile: DeviceFile, show = true, isView) => ({
  type : EDIT_DEVICE_FILE, deviceFile, show, isView
});

/**
 * Invoked when the the Device File auditor is to be displayed or removed.
 */
export const auditDeviceFile = (deviceFile: DeviceFile, show = true) => ({
  type : AUDIT_DEVICE_FILE, deviceFile, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_DEVICE_FILE_SHOW_CHANGES_DIALOG, auditRecord, show
});

export const downloadFile = (deviceFile: DeviceFile, fileType, show = true) => ({
  type : DOWNLOAD_FILE, deviceFile, fileType, show
});

export const downloadReleaseNote = (deviceFile: DeviceFile, show = true) => ({
  type : DOWNLOAD_RELEASE_NOTE, deviceFile, show
});

/**
 * @addFile
 *
 * Invoked when a new device file is to be created, delegating to the create devicefile
 * CTC API and dispatching a successful response or error action accordingly.
 */

export const addFile = (file: DeviceFile, deviceFileData, releaseNoteData) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_DEVICE_FILE_REQUEST});
    try {
      const created = await createDeviceFile(file);
      resolveDeviceFile(created, deviceFileData, releaseNoteData);
      dispatch({type: ADD_DEVICE_FILE_RESPONSE, file: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.file)));
    } catch (err) {
      dispatch({type: ADD_DEVICE_FILE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.file, err)));
      error(err);
    }
  };
};

/**
 * Invoked when a new device file is to be updated, delegating to the update devicefile
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editFile = ({
  file,
  deviceFileData,
  releaseFileData,
  autojobsFlagEnabled = false
}) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_DEVICE_FILE_REQUEST});
    try {
      const fileResult      = await getFile(file?.id);
      file.version          = fileResult?.[0]?.version;
      const updated         = await updateDeviceFile(file, autojobsFlagEnabled);
      const updatedFile     = updated?.[0];
      const metadata        = updatedFile.validationMetaData || {};
      resolveDeviceFile(updated?.[0], deviceFileData, releaseFileData);
      dispatch({type: EDIT_DEVICE_FILE_RESPONSE, deviceFile: updated});
      const {
        autoJobDevicesAffected: count, autoJobId, fields
      } = metadata;
      const autoJobsEnabled = !!fields?.updateAssignedDevices;
      const includeMessage  = count && autoJobsEnabled;
      const message         = includeMessage ? translate('devicefiles:editors.autojobs.dialog.successMessage', {
        id : autoJobId, count
      }) : undefined;
      dispatch(toggleUserPrompt({
        ...successEditOptions(Entity.file),
        ...message
      }));
    } catch (err) {
      dispatch({type: EDIT_DEVICE_FILE_ERROR, error: err});
      dispatch(toggleUserPrompt({
        ...errorOptions,
        message       : err?.errorText?.error?.description || err?.errorText || errorEditOptions(Entity.file),
        translateText : false
      }));
      error(err);
    }
  };
};

export const createModularSearchFilters = (search: DeviceFileFilters) => {
  const result = Object.entries(search)
    .filter(([, value]) => !isNull(value))
    .reduce((acc, [key, value]) => {
      switch (key) {
        case SearchFields.TYPE:
          return {
            ...acc,
            [SearchFields.TYPE] : value.split(', ')
          };
        case SearchFields.FILE_VERSION:
          return {
            ...acc,
            validationMetaData : {
              fields : {
                ...acc.validationMetaData?.fields,
                [SearchFields.FILE_VERSION] : `${value}`
              }
            }
          };
        case SearchFields.STATUS:
          return {
            ...acc,
            validationStatus : {
              ...acc.validationStatus,
              [SearchFields.STATUS] : `${value}`
            }
          };
        case SearchFields.APP_ID:
          return {
            ...acc,
            validationMetaData : {
              fields : {
                ...acc.validationMetaData?.fields,
                [SearchFields.APP_ID] : [{value: `${value}`}]
              }
            }
          };
        default:
          return {...acc, [key]: value};
      };
    }, {});
  if (result[SearchFields.TYPE]) {
    result.validationMetaData = result[SearchFields.TYPE].map((value: string) => {
      const withStringValue = safeJSONStringify(
        result.validationMetaData
          ? {
            fields : {
              ...result.validationMetaData?.fields
            },
            [SearchFields.TYPE] : value
          } : {
            [SearchFields.TYPE] : value
          }
      );
      const withIntegerValue = safeJSONStringify(
        result.validationMetaData
          ? {
            fields : {
              ...result.validationMetaData?.fields
            },
            [SearchFields.TYPE] : parseInt(value)
          } : {
            [SearchFields.TYPE] : parseInt(value)
          }
      );
      return [withStringValue, withIntegerValue];
    }).join(',');
    delete result[SearchFields.TYPE];
  } else if (result.validationMetaData) {
    result.validationMetaData = safeJSONStringify(result.validationMetaData);
  }
  if (result.validationStatus) {
    result.validationStatus = safeJSONStringify(result.validationStatus);
  }
  return result;
};

/**
 * @getDeviceFiles
 *
 * fetches device files for this account to be displayed in the Devices
 * section
 */
export const getDeviceFiles = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  callback: callbackFn,
  includeSubGroups,
  accountId,
  includeOnlyThisAccount
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_DEVICE_FILES_REQUEST});
    const {
      devicefiles: {
        search
      }
    } = getState();
    const data: LoadDeviceFilesParams = {
      pageId,
      pageSize,
      includeSubGroups,
      accountId,
      includeOnlyThisAccount
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {items, totalCount} = await loadDeviceFiles(data);
      dispatch({type: GET_DEVICE_FILES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_DEVICE_FILES_ERROR, error: err});
      return error(err);
    }
  };
};

/**
 * @viewDeviceFiles
 *
 * Invoked when the View deviceFile editor is to be displayed or removed.
 */
export const viewDeviceFiles = (deviceFile: DeviceFile, show = true) => ({
  type : VIEW_DEVICE_FILES, deviceFile, show
});

// Dispatches an action to show or hide the DeviceFile deletion confirmation dialog to the user
export const displayDeleteDialog = (deviceFile: DeviceFile, show = true) => ({
  type : DELETE_DEVICE_FILE, deviceFile, show
});

// Dispatches actions related to deleting a DeviceFile
export const deleteFile = (deviceFile: DeviceFile) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_DEVICE_FILE_REQUEST});
    try {
      const deleted = await deleteDeviceFile(deviceFile);
      dispatch({type: DELETE_DEVICE_FILE_RESPONSE, deviceFile: deleted});
    } catch (err) {
      dispatch({type: DELETE_DEVICE_FILE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setDeviceFilesViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user click on View Only This Accounts toggle button
 */
export const setDeviceFilesViewOnlyThisAccount = (viewOnlyThisAccount: boolean = false) => ({
  type : SET_VIEW_ONLY_THIS_ACCOUNT, viewOnlyThisAccount
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setDeviceFilesGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
